export type FacetType = 'refinementList' | 'menu';

/**
 * This function creates a search url, specifically for searchkit queries.
 *
 * @param pathname - pathname of the url that prefixes the query params
 * @param indexName - the searchkit index
 * @param query - the search query
 * @returns url
 */
export function createQueryUrl(pathname: string, indexName: string, query: string) {
  const param = encodeURIComponent(`${indexName}[query]`);
  const encodedQuery = encodeURIComponent(query);

  return `${pathname}?${param}=${encodedQuery}`;
}

export interface CreateSearchUrlParamProps {
  index: string;
  args: (string | number)[];
  value: string;
}

/**
 * This function creates a search url param from a given set of arguments.
 * Useful for creating a search facet url, among other things.
 *
 * @param index - the searchkit index
 * @param args - the url param arguments to include (e.g. attribute, facetType)
 * @param value - the param value
 * @returns search param
 */
export function createSearchUrlParam(
  index: CreateSearchUrlParamProps['index'],
  args: CreateSearchUrlParamProps['args'],
  value: CreateSearchUrlParamProps['value'],
) {
  const formattedArgs = args.map((arg) => `[${arg}]`);
  return `${index}${encodeURIComponent(formattedArgs.join(''))}=${encodeURIComponent(value)}`;
}

export interface CreateSearchFacetUrlProps extends Pick<CreateSearchUrlParamProps, 'index' | 'value'> {
  pathname: string;
  facetType: FacetType;
  attribute: string;
}

/**
 * This function creates a search url, specifically for searchkit facets,
 * using the facet type and attribute.
 *
 * @param options -
 *   pathname {string} - pathname of the url that prefixes the query params
 *   index {string} - the searchkit index
 *   facetType {'refinementList' | 'menu'} - the type of facet to target
 *   attribute {string} - nane of the attribute to target
 *   value {string} - the param value
 * @returns url
 */
export function createSearchFacetUrl({
  pathname,
  index,
  facetType,
  attribute,
  value,
}: CreateSearchFacetUrlProps) {
  const args = [facetType, attribute, ...(facetType === 'refinementList' ? [0] : [])];
  const param = createSearchUrlParam(index, args, value);
  return `${pathname}?${param}`;
}
